import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Loader2, 
  Send,
  Mail,
  Clock,
  CheckCircle2,
  FileText,
  Plus,
  Edit,
  Users,
  AlertCircle,
  UserCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "all", label: "All Regions" },
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const STATUSES = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Not Started" },
  { value: "started", label: "In Progress" },
  { value: "completed", label: "Completed" }
];

export default function CommunicationHub({ tokenKey = "chroToken", userRole = "chro" }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [stats, setStats] = useState({ notStarted: 0, inProgress: 0, pendingValidation: 0 });
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [recipientRegion, setRecipientRegion] = useState("all");
  const [recipientStatus, setRecipientStatus] = useState("all");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  
  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  
  // Detail modal
  const [selectedComm, setSelectedComm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchRecipientCount();
  }, [recipientRegion, recipientStatus]);

  const fetchData = async () => {
    try {
      const [templatesRes, historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/communications/templates`, getAuthHeaders()),
        axios.get(`${API}/communications/history`, getAuthHeaders()),
        axios.get(`${API}/communications/stats`, getAuthHeaders()).catch(() => ({ data: { notStarted: 0, inProgress: 0, pendingValidation: 0 } }))
      ]);
      
      setTemplates(templatesRes.data.templates);
      setHistory(historyRes.data.communications);
      setStats(statsRes.data || { notStarted: 0, inProgress: 0, pendingValidation: 0 });
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load communication data");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipientCount = async () => {
    try {
      const params = new URLSearchParams();
      if (recipientRegion !== "all") params.append("region", recipientRegion);
      if (recipientStatus !== "all") params.append("status", recipientStatus);
      
      const response = await axios.get(`${API}/communications/recipient-count?${params}`, getAuthHeaders());
      setRecipientCount(response.data.count);
    } catch (err) {
      console.error("Error fetching recipient count:", err);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.message);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please enter subject and message");
      return;
    }

    if (recipientCount === 0) {
      toast.error("No recipients match the selected filters");
      return;
    }

    try {
      setSending(true);
      const response = await axios.post(`${API}/communications/send`, {
        subject,
        message,
        template_used: selectedTemplate || null,
        recipient_filter: {
          region: recipientRegion,
          status: recipientStatus
        }
      }, getAuthHeaders());
      
      toast.success(response.data.message);
      
      // Reset form
      setSubject("");
      setMessage("");
      setSelectedTemplate("");
      setRecipientRegion("all");
      setRecipientStatus("all");
      
      // Refresh history
      fetchData();
      setShowPreview(false);
    } catch (err) {
      console.error("Error sending communication:", err);
      toast.error("Failed to send communication");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilterLabel = (filter) => {
    if (!filter) return "All";
    const parts = [];
    if (filter.region && filter.region !== 'all') {
      parts.push(REGIONS.find(r => r.value === filter.region)?.label || filter.region);
    }
    if (filter.status && filter.status !== 'all') {
      parts.push(STATUSES.find(s => s.value === filter.status)?.label || filter.status);
    }
    return parts.length > 0 ? parts.join(', ') : 'All Employees';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a365d]" />
      </div>
    );
  }

  return (
    <main className="max-w-[1600px] mx-auto px-6 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1a365d]">Communication Hub</h1>
        <p className="text-muted-foreground">Send reminders and updates to assessment participants</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Compose */}
        <div className="lg:col-span-2 space-y-6">
          {/* Compose Message */}
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#1a365d] flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send Communication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Recipients */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Recipients</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Region</Label>
                    <Select value={recipientRegion} onValueChange={setRecipientRegion}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
                    <Select value={recipientStatus} onValueChange={setRecipientStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    This will be sent to <strong>{recipientCount}</strong> employees
                  </span>
                </div>
              </div>

              {/* Template Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Template (Optional)</Label>
                <Select value={selectedTemplate || "custom"} onValueChange={(val) => handleTemplateSelect(val === "custom" ? "" : val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a template or write custom message" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Message</SelectItem>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject"
                  data-testid="comm-subject"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Message</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your message..."
                  className="min-h-[200px]"
                  data-testid="comm-message"
                />
                <p className="text-xs text-muted-foreground">
                  Variables: [Employee Name], [Unique Link], [Deadline Date] will be automatically replaced
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPreview(true)}
                  disabled={!subject || !message}
                >
                  Preview
                </Button>
                <Button 
                  className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                  onClick={() => setShowPreview(true)}
                  disabled={!subject || !message || recipientCount === 0}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Communication History */}
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#1a365d]">Communication History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No communications sent yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Subject</th>
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Recipients</th>
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Region</th>
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Open Rate</th>
                        <th className="pb-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((comm) => (
                        <tr 
                          key={comm.id} 
                          className="border-b hover:bg-slate-50 cursor-pointer"
                          onClick={() => setSelectedComm(comm)}
                        >
                          <td className="py-3 text-sm">{formatDate(comm.sentAt)}</td>
                          <td className="py-3 text-sm font-medium">{comm.subject}</td>
                          <td className="py-3 text-sm">{comm.recipientCount}</td>
                          <td className="py-3 text-sm">
                            <Badge variant="outline" className="text-xs">
                              {getFilterLabel(comm.recipientFilter)}
                            </Badge>
                          </td>
                          <td className="py-3">
                            {comm.openRate ? (
                              <span className="text-sm font-medium text-emerald-600">{comm.openRate}%</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3">
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {comm.status === 'sent' ? 'Delivered' : comm.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Quick Actions & Templates */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#1a365d]">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3 hover:bg-red-50 hover:border-red-200"
                onClick={() => {
                  setRecipientStatus('pending');
                  setSelectedTemplate('reminder_not_started');
                  handleTemplateSelect('reminder_not_started');
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <span className="font-medium">Remind Not Started</span>
                </div>
                <Badge variant="destructive" className="text-xs">{stats?.notStarted || 0}</Badge>
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3 hover:bg-amber-50 hover:border-amber-200"
                onClick={() => {
                  setRecipientStatus('started');
                  setSelectedTemplate('reminder_in_progress');
                  handleTemplateSelect('reminder_in_progress');
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <span className="font-medium">Remind In Progress</span>
                </div>
                <Badge className="bg-amber-500 text-xs">{stats?.inProgress || 0}</Badge>
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3 hover:bg-blue-50 hover:border-blue-200"
                onClick={() => {
                  setSelectedTemplate('manager_validation');
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium">Notify Managers</span>
                </div>
                <Badge className="bg-blue-500 text-xs">{stats?.pendingValidation || 0}</Badge>
              </Button>
            </CardContent>
          </Card>

          {/* Templates */}
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#1a365d]">Message Templates</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div 
                      key={template.id}
                      className="p-4 rounded-lg border hover:border-[#1a365d]/30 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{template.name}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {template.message.substring(0, 100)}...
                          </p>
                          {template.lastUsedAt && (
                            <p className="text-xs text-muted-foreground mt-2">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Last used: {formatDate(template.lastUsedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplateSelect(template.id);
                          }}
                        >
                          Use Template
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Create New Template */}
                  <div className="p-4 rounded-lg border-2 border-dashed border-slate-200 hover:border-[#1a365d]/30 transition-colors cursor-pointer text-center">
                    <Plus className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Create New Template</p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview/Confirm Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview & Confirm</DialogTitle>
            <DialogDescription>
              Review your message before sending to {recipientCount} employees
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">TO</p>
              <p className="font-medium">
                {getFilterLabel({ region: recipientRegion, status: recipientStatus })} ({recipientCount} employees)
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">SUBJECT</p>
              <p className="font-medium">{subject}</p>
            </div>
            
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">MESSAGE</p>
              <div className="whitespace-pre-wrap text-sm">{message}</div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {recipientCount} Employees
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication Detail Modal */}
      <Dialog open={!!selectedComm} onOpenChange={() => setSelectedComm(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Communication Details</DialogTitle>
          </DialogHeader>
          
          {selectedComm && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">SENT BY</p>
                  <p className="font-medium">{selectedComm.sentByName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">DATE</p>
                  <p className="font-medium">{formatDate(selectedComm.sentAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">RECIPIENTS</p>
                  <p className="font-medium">{selectedComm.recipientCount} employees</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">STATUS</p>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Delivered
                  </Badge>
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">SUBJECT</p>
                <p className="font-medium">{selectedComm.subject}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedComm(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
